
import { useState } from 'react';

interface OperationDetailProps {
  operationId: string | null;
  onUploadDocument?: () => void;
  onUpdateStatus?: () => void;
}

export default function OperationDetail({ operationId, onUploadDocument, onUpdateStatus }: OperationDetailProps) {
  const [activeTab, setActiveTab] = useState('timeline');

  if (!operationId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 flex flex-col items-center justify-center h-full min-h-[600px]">
        <div className="w-16 h-16 rounded-full bg-[#EAF2F8] flex items-center justify-center mb-4">
          <i className="ri-file-list-3-line text-3xl text-[#2F80ED]"></i>
        </div>
        <p className="text-sm text-[#6B7280] text-center">
          Selecciona una operación para ver los detalles
        </p>
      </div>
    );
  }

  const bitacora = [
    { user: 'María González', action: 'Actualizó estado a "En Tránsito"', date: '2024-06-05 14:30', type: 'status' },
    { user: 'Sistema', action: 'Documento BL verificado automáticamente', date: '2024-06-04 09:15', type: 'system' },
    { user: 'Carlos Ramírez', action: 'Subió Factura Comercial', date: '2024-06-03 16:45', type: 'document' },
    { user: 'Ana Martínez', action: 'Creó la operación', date: '2024-05-28 10:00', type: 'create' },
  ];

  const documents = [
    { name: 'Bill of Lading', status: 'Aprobado', icon: 'ri-file-text-line', date: '2024-06-01' },
    { name: 'Factura Comercial', status: 'Aprobado', icon: 'ri-file-text-line', date: '2024-06-03' },
    { name: 'Certificado de Origen', status: 'Pendiente', icon: 'ri-file-text-line', date: '-' },
    { name: 'Packing List', status: 'Aprobado', icon: 'ri-file-text-line', date: '2024-06-02' },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#114B7A] to-[#2F80ED]">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">{operationId}</h3>
          <div className="flex gap-2">
            <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-white/20 transition-colors cursor-pointer">
              <i className="ri-printer-line text-white"></i>
            </button>
            <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-white/20 transition-colors cursor-pointer">
              <i className="ri-share-line text-white"></i>
            </button>
          </div>
        </div>
        <p className="text-sm text-white/80 mt-1">Importadora Global S.A.</p>
        <div className="flex items-center gap-2 mt-2">
          <span className="px-2 py-1 text-xs font-medium bg-white/20 text-white rounded-md">Importación</span>
          <span className="px-2 py-1 text-xs font-medium bg-[#10B981] text-white rounded-md">En Tránsito</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {[
          { id: 'timeline', label: 'Timeline', icon: 'ri-time-line' },
          { id: 'documents', label: 'Documentos', icon: 'ri-file-list-line' },
          { id: 'bitacora', label: 'Bitácora', icon: 'ri-history-line' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-3 text-xs font-medium flex items-center justify-center gap-1 transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab.id
                ? 'text-[#2F80ED] border-b-2 border-[#2F80ED] bg-[#EAF2F8]'
                : 'text-[#6B7280] hover:bg-gray-50'
            }`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4 max-h-[calc(100vh-400px)] overflow-y-auto">
        {activeTab === 'timeline' && (
          <div className="space-y-4">
            {/* Status Progress */}
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase">Progreso</label>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full w-3/4 bg-gradient-to-r from-[#114B7A] to-[#2F80ED]"></div>
                </div>
                <span className="text-xs font-semibold text-[#2F80ED]">75%</span>
              </div>
            </div>

            {/* Timeline */}
            <div className="mt-4 space-y-3">
              {[
                { label: 'Documentación Completa', date: '2024-05-28', status: 'completed' },
                { label: 'Embarque Confirmado', date: '2024-06-01', status: 'completed' },
                { label: 'En Tránsito', date: '2024-06-05', status: 'current' },
                { label: 'Llegada a Puerto', date: '2024-06-15', status: 'pending' },
                { label: 'Despacho Aduanero', date: 'Pendiente', status: 'pending' },
                { label: 'Entrega Final', date: 'Pendiente', status: 'pending' },
              ].map((item, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        item.status === 'completed'
                          ? 'bg-[#10B981] text-white'
                          : item.status === 'current'
                          ? 'bg-[#2F80ED] text-white animate-pulse'
                          : 'bg-gray-200 text-[#6B7280]'
                      }`}
                    >
                      {item.status === 'completed' ? (
                        <i className="ri-check-line"></i>
                      ) : item.status === 'current' ? (
                        <i className="ri-ship-line"></i>
                      ) : (
                        <i className="ri-time-line"></i>
                      )}
                    </div>
                    {index < 5 && (
                      <div className={`w-0.5 h-8 ${item.status === 'completed' ? 'bg-[#10B981]' : 'bg-gray-200'}`}></div>
                    )}
                  </div>
                  <div className="flex-1 pb-2">
                    <p className={`text-sm font-semibold ${item.status === 'pending' ? 'text-[#6B7280]' : 'text-[#0A2A43]'}`}>{item.label}</p>
                    <p className="text-xs text-[#6B7280] mt-1">{item.date}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-[#6B7280]">Valor</p>
                <p className="text-sm font-bold text-[#0A2A43]">$125,400 USD</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-[#6B7280]">ETA</p>
                <p className="text-sm font-bold text-[#0A2A43]">15 Jun 2024</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-[#6B7280]">Origen</p>
                <p className="text-sm font-bold text-[#0A2A43]">Shanghai, CN</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-[#6B7280]">Destino</p>
                <p className="text-sm font-bold text-[#0A2A43]">Callao, PE</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-3">
            {documents.map((doc, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  doc.status === 'Aprobado' ? 'bg-[#10B981]/10' : 'bg-[#EAB308]/10'
                }`}>
                  <i className={`${doc.icon} ${doc.status === 'Aprobado' ? 'text-[#10B981]' : 'text-[#EAB308]'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0A2A43]">{doc.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      doc.status === 'Aprobado' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#EAB308]/10 text-[#EAB308]'
                    }`}>{doc.status}</span>
                    <span className="text-xs text-[#6B7280]">{doc.date}</span>
                  </div>
                </div>
                <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-white transition-colors cursor-pointer">
                  <i className="ri-download-line text-[#6B7280]"></i>
                </button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'bitacora' && (
          <div className="space-y-3">
            {bitacora.map((entry, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    entry.type === 'status' ? 'bg-[#2F80ED]/10 text-[#2F80ED]' :
                    entry.type === 'document' ? 'bg-[#10B981]/10 text-[#10B981]' :
                    entry.type === 'system' ? 'bg-gray-200 text-[#6B7280]' :
                    'bg-[#114B7A]/10 text-[#114B7A]'
                  }`}>
                    <i className={
                      entry.type === 'status' ? 'ri-refresh-line' :
                      entry.type === 'document' ? 'ri-file-upload-line' :
                      entry.type === 'system' ? 'ri-robot-line' :
                      'ri-add-circle-line'
                    }></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-[#0A2A43]">
                      <span className="font-semibold">{entry.user}</span>
                    </p>
                    <p className="text-xs text-[#6B7280] mt-1">{entry.action}</p>
                    <p className="text-xs text-gray-400 mt-1">{entry.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-200 space-y-2">
        <button 
          onClick={onUploadDocument}
          className="w-full py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-md font-medium hover:shadow-md transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-upload-line mr-2"></i>Subir Documento
        </button>
        <button 
          onClick={onUpdateStatus}
          className="w-full py-2.5 border border-gray-200 text-[#6B7280] rounded-md font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-line mr-2"></i>Actualizar Estado
        </button>
      </div>
    </div>
  );
}
